import {createSelector} from "reselect";

const controllers = (state) => state.controllers;

export const getControllersList = createSelector(controllers, (controllers) => {
  const controllerList = controllers.controllersList || [];
  // Note: Negating alarm to show green color for false and red color for true in table.
  // As logic written in table is opposite of this
  return controllerList.map((controller) => ({
    ...controller,
    alarm: typeof controller.alarm === "boolean" ? !controller.alarm : controller.alarm,
  }));
});

export const getEthernetConfigurations = createSelector(
  controllers,
  (controllers) => controllers.etheretConfigurations
);

export const getWifiConfigurations = createSelector(
  controllers,
  (controllers) => controllers.wifiConfigurations
);

export const getSelectedController = createSelector(
  controllers,
  (controllers) => controllers.selectedController || {}
);

export const getAllProperties = createSelector(
  getSelectedController,
  (data) => data?.properties
);

export const getSummaryData = createSelector(
  controllers,
  (controllers) => controllers.summary
);

export const getFormattedSummaryData = createSelector(getSummaryData, (data) => {
  let sensorsGroupData = {};
  const headers = {
    input: ["description", "type", "value", "pin", "is_powered_on"],
    // Modify table headers if Input and Output needs different set of headers.
    output: ["description", "type", "value", "runtime", "pin"],
  };

  for (let sensorGroup in data) {
    let sensorsObject = {};
    for (let ioType of data[sensorGroup]) {
      for (let sensorType in ioType) {
        let sensors = [];
        const sensorItems = ioType[sensorType];
        sensorItems.forEach((item) => {
          sensors.push({
            title: item.name,
            data: item.sensors,
            sensorType: sensorType,
            headers: headers[sensorType],
          });
        });
        sensorsObject[sensorType] = sensors;
      }
    }
    sensorsGroupData[sensorGroup] = sensorsObject;
  }
  return sensorsGroupData;
});

export const getInputSensors = createSelector(
  getFormattedSummaryData,
  (summaryData = {}) => {
    let inputSensors = [];
    for (let key in summaryData) {
      const sensorGroup = summaryData[key];
      const {input = []} = sensorGroup;
      input.forEach((eachInputType) => {
        const {data = []} = eachInputType;
        inputSensors = [...inputSensors, ...data];
      });
    }
    return inputSensors;
  }
);

export const getInputSensorLabelValue = createSelector(
  getInputSensors,
  (sensors = []) => {
    return sensors.map((sensor) => ({label: sensor.description, value: sensor.id}));
  }
);

export const getOutputSensors = createSelector(
  getFormattedSummaryData,
  (summaryData = {}) => {
    let outputSensors = [];
    for (let key in summaryData) {
      const sensorGroup = summaryData[key];
      const {output = []} = sensorGroup;
      output.forEach((eachOutputType) => {
        const {data = []} = eachOutputType;
        outputSensors = [...outputSensors, ...data];
      });
    }
    return outputSensors;
  }
);

export const getOutputSensorLabelValue = createSelector(
  getOutputSensors,
  (sensors = []) => {
    return sensors.map((sensor) => ({label: sensor.description, value: sensor.id}));
  }
);

export const getSensors = createSelector(
  getInputSensors,
  getOutputSensors,
  (inputSensors = [], outputSensors = []) => {
    return [...inputSensors, ...outputSensors];
  }
);

export const getSensorsLabelValue = createSelector(
  getInputSensorLabelValue,
  getOutputSensorLabelValue,
  (inputSensorsLabelValue = [], outputSensorsLabelValue = []) => {
    return [...inputSensorsLabelValue, ...outputSensorsLabelValue];
  }
);

export const getSystemLogsObject = createSelector(
  controllers,
  (controller) => controller.systemLogs
);
export const getSystemLogs = createSelector(getSystemLogsObject, (logs) => {
  if (logs?.result?.length > 0) {
    return logs.result.map((log) => ({
      ...log,
      timestamp: log._time,
      message: log._value,
    }));
  }
  return {};
});

export const getLogsTotalCount = createSelector(
  getSystemLogsObject,
  (logs) => logs.total_count
);

export const getLogsPaginationUrls = createSelector(getSystemLogsObject, (logs) => ({
  prevUrl: logs.previous,
  nextUrl: logs.next,
}));

export const getSensorSummary = createSelector(
  controllers,
  (controller) => controller.sensorSummary
);

export const getAnalogBanks = createSelector(
  controllers,
  (controller) => controller.analogBanks
);

export const getFavourite = createSelector(
  controllers,
  (controller) => controller.favourite
);

export const getFavourites = createSelector(controllers, (controllerState) => {
  const {favourites} = controllerState;
  if (favourites?.length > 0) {
    return favourites?.map((controller) => ({
      ...controller,
      alarm: typeof controller.alarm === "boolean" ? !controller.alarm : controller.alarm,
    }));
  } else {
    return [];
  }
});

export const getInventorySensorSummary = createSelector(
  controllers,
  (controllers) => controllers.sensorData || {}
);

export const getSensorCalibrationCommands = createSelector(
  controllers,
  (controllers) => controllers.calibrationCmds
);

export const getCalibrationLogsObject = createSelector(
  controllers,
  (controller) => controller.calibrationHistory
);

export const getCalibrationLogs = createSelector(getCalibrationLogsObject, (logs) => {
  if (logs?.result?.length > 0) {
    return logs.result.map((log) => ({
      ...log,
      timestamp: log._time,
      message: log._value,
    }));
  }
  return {};
});

export const getCategoriesList = createSelector(controllers, (controllers) => {
  return (
    controllers.categoryList?.results?.map((category) => ({
      label: category.name,
      value: category.id,
    })) || []
  );
});

const getControllersWithActiveAlertData = createSelector(
  controllers,
  (data) => data?.controllersWithActiveAlert || []
);

export const getControllersWithActiveAlert = createSelector(
  getControllersWithActiveAlertData,
  (data) => {
    const controllers = data || [];
    if (controllers.length > 0) {
      return controllers.map((item) => {
        const {inventory = {}} = item;
        return {
          ...item,
          inventory: {...inventory},
          claimed_by: inventory.claimed_by,
          location_name: inventory.location_name,
          name: inventory.name,
          serial: inventory.serial,
          site_name: inventory.site_name,
          state: inventory.properties?.state?.state,
          alarm:
            typeof inventory.alarm === "boolean" ? !inventory.alarm : inventory.alarm,
          alert_type:
            item.alert_type === "alertdefinition" ? "System Alert" : "Sensor Alert",
          alert_level: item.level,
        };
      });
    } else {
      return [];
    }
  }
);

export const getSnapshotData = createSelector(controllers, (data = {}) => {
  return data.snapshot || {};
});

export const snapshots = createSelector(getSnapshotData, (data) => data.results || []);

export const getSnapshotTotalCount = createSelector(
  getSnapshotData,
  (data) => data.count
);

export const getSnapshotPaginationUrls = createSelector(getSnapshotData, (data) => ({
  prevUrl: data.previous,
  nextUrl: data.next,
}));

export const getControllerSensorData = createSelector(
  controllers,
  (controllers) => controllers.controllerSensorData || []
);
